import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button, TextField } from '../components/ui'
import { useAuth } from '../components/AuthProvider'
import { useI18n } from '../i18n'
import { PageLayout } from '../layouts/PageLayout'
import EnterpriseOperationalPage from '../components/enterprise-operational/EnterpriseOperationalPage'
import EnterpriseOperationalSection from '../components/enterprise-operational/EnterpriseOperationalSection'
import { featureFlags } from '../config/featureFlags'
import entStyles from '../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '@/styles/pages/Settings.module.scss'

export default function Settings() {
  const { pathname } = useLocation()
  const isAdminSettings = pathname.includes('/admin/settings')
  const { user } = useAuth()
  const { t } = useI18n()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [dark, setDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme')
      if (saved === 'dark') return true
      if (saved === 'light') return false
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return false
  })

  useEffect(() => {
    const u =
      user ||
      (() => {
        const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null
        return raw
          ? (() => {
              try {
                return JSON.parse(raw)
              } catch {
                return null
              }
            })()
          : null
      })()
    if (u?.fullName) setName(u.fullName)
    else if (u?.username) setName(u.username)
    if (u?.email) setEmail(u.email)
  }, [user])

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark)
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', dark ? 'dark' : 'light')
    }
  }, [dark])

  function submit(e: React.FormEvent) {
    e.preventDefault()
    alert(t('settings.savedPlaceholder', 'Settings saved!'))
  }

  const profileForm = (
    <form className={featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? entStyles.formStack : styles.formStack} onSubmit={submit}>
      <TextField
        id="settings-name"
        label={t('settings.name', 'Name')}
        value={name}
        onChange={setName}
        autoComplete="name"
        minWidth="100%"
      />
      <TextField
        id="settings-email"
        label={t('settings.email', 'Email')}
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
        minWidth="100%"
      />
      <TextField
        id="settings-password"
        label={t('settings.password', 'Password')}
        type="password"
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
        placeholder={t('settings.passwordPlaceholder', 'Leave blank to keep current')}
        minWidth="100%"
      />
      <div className={featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? entStyles.formActions : styles.submitRow}>
        <Button type="submit" color={featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? 'outline' : 'primary'} size="small">
          {t('settings.save', 'Save changes')}
        </Button>
      </div>
    </form>
  )

  const appearancePanel = (
    <div className={featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? entStyles.formStack : styles.formStack}>
      <div className={featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? entStyles.themeRow : styles.themeRow}>
        <span className={featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? entStyles.postureMetricLabel : styles.themeLabel}>
          {t('settings.appearance', 'Appearance')}
        </span>
        <div className={styles.themeOptions}>
          <button
            type="button"
            className={`${styles.themeSwitch} ${dark ? styles.themeSwitchOn : styles.themeSwitchOff}`}
            onClick={() => setDark((v) => !v)}
            aria-pressed={dark}
          >
            <span className={`${styles.themeThumb} ${dark ? styles.themeThumbOn : styles.themeThumbOff}`} />
            <span className={styles.visuallyHidden}>{t('settings.darkMode', 'Dark mode')}</span>
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <PageLayout
      pageScope="settings-page"
      title={isAdminSettings ? t('nav.adminSettings', 'Admin settings') : t('nav.userSettings', 'User Settings')}
      description={t('settings.description', 'Manage your account settings and preferences')}
      contentClassName={styles.pageContent}
    >
      <div className={styles.inner}>
        {featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? (
          <EnterpriseOperationalPage>
            <EnterpriseOperationalSection
              layerLabel={t('settings.layerLabel', 'Account & preferences')}
              title={t('settings.profile', 'Profile')}
              subtitle={t('settings.profileHint', 'Update how you appear in the app')}
              testId="settings-profile-enterprise"
            >
              {profileForm}
            </EnterpriseOperationalSection>
            <EnterpriseOperationalSection
              layerLabel={t('settings.appearanceLayer', 'Display')}
              title={t('settings.appearance', 'Appearance')}
              subtitle={t('settings.appearanceHint', 'Choose light or dark interface theme')}
              testId="settings-appearance-enterprise"
            >
              {appearancePanel}
            </EnterpriseOperationalSection>
          </EnterpriseOperationalPage>
        ) : (
          <>
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <h2 className={styles.panelTitle}>{t('settings.profile', 'Profile')}</h2>
                <p className={styles.panelHint}>{t('settings.profileHint', 'Update how you appear in the app')}</p>
              </div>
              <div className={styles.panelBody}>{profileForm}</div>
            </div>
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <h2 className={styles.panelTitle}>{t('settings.appearance', 'Appearance')}</h2>
              </div>
              <div className={styles.panelBody}>{appearancePanel}</div>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  )
}
